import { createAsyncThunk } from '@reduxjs/toolkit'

import { MAX_SCORE } from '../constants/constants'
import { AppDispatch, RootState } from '../reducers'
import currentPlaySlice from '../reducers/currentPlaySlice'
import matchStatusSlice from '../reducers/matchStatusSlice'
import {
  selectCurrentPlaylistInfo,
  selectCurrentTrackDuration,
  selectCurrentTrackSlug,
  selectGamerStatus,
} from '../selectors/current-play-selectors'
import {
  selectOwnedTrackCounters,
  selectPeerPlaylistInfo,
  selectPlayerScore,
} from '../selectors/match-selectors'
import {
  selectCurrentSessionInfo,
  selectIsCurrentOwnedMatch,
  selectMode,
} from '../selectors/session-selectors'
import realtime from '../services/RealtimeService'
import {
  recalc,
  incrementalRecalc,
  getPlaylistScore,
  getMatchScore,
  getScoreRank,
} from '../util/score-utils'
import {
  PlayStatus,
  PlayStatusScoreInfo,
  ScoreBonuses,
  ScoreDelta,
  Section,
  TrackPath,
} from '../types'
import Util from '../util/util'

export const adjustTrackScore = createAsyncThunk<
  void,
  {
    trackPath: TrackPath
    totalScore: number
    bonuses: ScoreBonuses
  },
  { state: RootState; dispatch: AppDispatch }
>('playAction/adjustTrackScore2', ({ totalScore, bonuses, trackPath }, { dispatch, getState }) => {
  const { compoundMatchSlug, compoundPlayerSlug, playlistSlug, trackSlug } = trackPath
  if (!trackSlug || !playlistSlug) {
    return
  }
  Util.removeUndefinedProperties(bonuses) // otherwise can't store in firebase
  const { originalScore } = bonuses
  const [username, player] = compoundPlayerSlug.split('/')
  const [matchOwner, matchSlug] = compoundMatchSlug.split('/')
  const state = getState()
  const {
    matchScore: initialMatchScore,
    playlistScores: initialPlaylistScores,
    trackScores: initialTrackScores,
  } = selectPlayerScore({ matchOwner, matchSlug, player, username })(state)
  const playlistInfo = selectPeerPlaylistInfo(matchOwner, matchSlug, playlistSlug)(state)
  const currTrackPlayStatus = initialTrackScores[trackSlug]
  const newTopScoreRank = getScoreRank(originalScore, MAX_SCORE) // TODO: correctly compute more complicated "color you are you on track for"

  const newTrackPlayStatus = {
    ...currTrackPlayStatus,
    topScore: totalScore,
    topScoreRank: newTopScoreRank,
    score: totalScore,
    runningScoreRank: newTopScoreRank,
    bonuses: { ...bonuses },
    timestamp: Date.now(),
  }
  const trackScores = { ...initialTrackScores, [trackSlug]: newTrackPlayStatus }
  const newPlaylistPlayStatus = getPlaylistScore(playlistInfo, trackScores, true)
  const playlistScores = { ...initialPlaylistScores, [playlistSlug]: newPlaylistPlayStatus }
  const newMatchStatus = getMatchScore(playlistScores, false)
  newMatchStatus.song = initialMatchScore.song
  dispatch(
    matchStatusSlice.actions.updateTrackScore({
      player,
      username,
      matchSlug: compoundMatchSlug,
      trackSlug,
      playStatus: newTrackPlayStatus,
    })
  )
  dispatch(
    matchStatusSlice.actions.updatePlaylistScore({
      username,
      player,
      matchSlug: compoundMatchSlug,
      playlistSlug,
      playStatus: newPlaylistPlayStatus,
    })
  )
  dispatch(
    matchStatusSlice.actions.updateMatchScore({
      username,
      player,
      matchSlug: compoundMatchSlug,
      playStatus: newMatchStatus,
    })
  )
  const matchPath = `matches/${compoundMatchSlug}/players2/${username}/${player}`
  realtime.set(`${matchPath}/matchScore`, newMatchStatus)
  realtime.set(`${matchPath}/playlistScores/${playlistSlug}`, newPlaylistPlayStatus)
  realtime.set(`${matchPath}/trackScores/${trackSlug}`, newTrackPlayStatus)
})

const updateScore = createAsyncThunk<
  void,
  {
    gamerIndex: number
    sections: Section[]
    scoreSeconds: number
    scoreDelta: ScoreDelta | undefined
  },
  { state: RootState; dispatch: AppDispatch }
>(
  'playAction/updateScore',
  ({ gamerIndex, sections, scoreSeconds, scoreDelta }, { dispatch, getState }) => {
    const state = getState()
    const { username, currentMatchSlug: compoundMatchSlug, mode } = selectCurrentSessionInfo(state)
    const isCurrentOwnedMatch = selectIsCurrentOwnedMatch(state)
    if (isCurrentOwnedMatch && mode === 'play') {
      return
    }
    const [matchOwner, matchSlug] = compoundMatchSlug.split('/')
    const isEditMode = selectMode(state) === 'edit'
    const trackSlug = selectCurrentTrackSlug(state)
    const { slug: playlistSlug } = selectCurrentPlaylistInfo(state)
    const { gamerId: player, playStatus: currPlayStatus } = selectGamerStatus(gamerIndex)(state)
    const trackDuration = selectCurrentTrackDuration(state)
    const { topScore: currentTopScore } = currPlayStatus
    const numLaps =
      trackDuration && scoreSeconds > trackDuration ? Math.floor(scoreSeconds / trackDuration) : 0
    const newPlayStats: PlayStatusScoreInfo = scoreDelta
      ? incrementalRecalc(currPlayStatus, numLaps, scoreDelta)
      : recalc({
          gamerId: player,
          sections,
          currentTopScore,
          maxScore: MAX_SCORE,
          currentLapCount: numLaps,
          isUseDoneCount: isEditMode,
        })
    const {
      isNewHighScore,
      score,
      errorCount,
      doneCount,
      topScore,
      topScoreRank,
      counters: newCounters,
      // numElements,
    } = newPlayStats
    const runningScoreRank = getScoreRank(score, MAX_SCORE) // TODO: correctly compute more complicated "color you are you on track for"

    // Now we have to go out of our way to prevent our audience total counters from getting overwritten
    // Yeah, we're paying for overloading stuff here. TODO: break into separate state or something...
    const counters = isCurrentOwnedMatch ? selectOwnedTrackCounters(trackSlug)(state) : newCounters
    const roundedScore = Math.round(score * 1000) / 1000
    const roundedTopScore = Math.round(topScore * 1000) / 1000
    const newPlayStatus: PlayStatus = {
      ...currPlayStatus,
      score: roundedScore,
      numLaps,
      doneCount,
      // numElements,
      errorCount,
      topScore: roundedTopScore,
      topScoreRank,
      counters,
      scoreSeconds,
      timestamp: Date.now(),
      runningScoreRank,
    }
    dispatch(currentPlaySlice.actions.setPlayStatus({ gamerIndex, playStatus: newPlayStatus }))
    const trackScore = {
      player,
      username,
      matchSlug: compoundMatchSlug,
      trackSlug,
      playStatus: newPlayStatus,
    }
    if (isNewHighScore) {
      dispatch(matchStatusSlice.actions.updateTrackScore(trackScore))
      const playlistInfo = selectPeerPlaylistInfo(matchOwner, matchSlug, playlistSlug)(state)
      const { trackScores } = selectPlayerScore({
        username,
        player,
        matchOwner,
        matchSlug,
      })(getState())
      const playlistStatus = getPlaylistScore(playlistInfo, trackScores, !isCurrentOwnedMatch)
      const playlistScore = {
        username,
        player,
        matchSlug: compoundMatchSlug,
        playlistSlug,
        playStatus: playlistStatus,
      }
      dispatch(matchStatusSlice.actions.updatePlaylistScore(playlistScore))
      const currMatchStatus = selectPlayerScore({
        username,
        player,
        matchOwner,
        matchSlug,
      })(getState())
      const newMatchStatus = getMatchScore(currMatchStatus.playlistScores, !isCurrentOwnedMatch) // TODO: cleanup like above
      newMatchStatus.song = currMatchStatus.matchScore.song
      const matchScore = {
        username,
        player,
        matchSlug: compoundMatchSlug,
        playStatus: newMatchStatus,
      }
      dispatch(matchStatusSlice.actions.updateMatchScore(matchScore))
    }
  }
)

export default updateScore
